<template>
  <div class="askai-chat d-flex flex-column h-100">
    <div
      ref="scrollRef"
      class="chat-scroll flex-grow-1 overflow-auto p-3 border rounded mb-2"
      style="min-height: 12rem;"
    >
      <div v-if="!history.length" class="text-muted text-center fst-italic mt-3">
        Ask the AI assistant a question to get started.
      </div>
      <ChatMessage v-for="(m, i) in history" :key="i" :message="m" />
    </div>

    <form class="chat-input d-flex gap-2" @submit.prevent="sendMessage">
      <textarea
        v-model="input"
        class="form-control"
        rows="2"
        placeholder="Type your message…"
        :disabled="isStreaming"
        aria-label="Message"
        @keydown.enter.exact.prevent="sendMessage"
      ></textarea>
      <button
        type="submit"
        class="btn btn-primary align-self-end"
        :disabled="isStreaming || !input.trim()"
      >
        <span v-if="isStreaming" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
        <i v-else class="bi bi-send"></i>
        Send
      </button>
    </form>
  </div>
</template>

<script>
import ChatMessage from './ChatMessage.vue';
import ConversationController from '@/controllers/ConversationController';

export default {
  name: 'AskAIChat',
  components: { ChatMessage },
  props: {
    subtaskId: { type: [String, Number], default: null },
    conversationId: { type: [String, Number], default: null },
    messages: { type: Array, default: () => [] },
    // Teacher chat mode (#64): route to the teacher endpoint and forward systemPrompt.
    teacher: { type: Boolean, default: false },
    systemPrompt: { type: String, default: null },
  },
  emits: ['update:conversationId', 'conversation-created'],
  data() {
    return {
      history: [],
      input: '',
      isStreaming: false,
      localConversationId: this.conversationId || null,
    };
  },
  watch: {
    messages: {
      immediate: true,
      handler(newMessages) {
        // Don't clobber a live stream when the parent swaps props.
        if (this.isStreaming) return;
        this.history = Array.isArray(newMessages) ? newMessages.map((m) => ({ ...m })) : [];
      },
    },
    conversationId(newId) {
      this.localConversationId = newId || null;
    },
  },
  methods: {
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.scrollRef;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    async sendMessage() {
      const content = this.input.trim();
      if (!content || this.isStreaming) return;

      this.history.push({ role: 'user', source: 'user', content });
      this.input = '';
      const assistantIndex = this.history.push({ role: 'assistant', source: 'assistant', content: '' }) - 1;
      this.scrollToBottom();

      const subtaskId =
        this.subtaskId != null && this.subtaskId !== '' ? Number(this.subtaskId) : null;
      const wasNew = !this.localConversationId;
      this.isStreaming = true;
      try {
        const args = { message: content, conversationId: this.localConversationId, subtaskId };
        const callbacks = {
          onChunk: (delta) => {
            this.history[assistantIndex].content += delta;
            this.scrollToBottom();
          },
          onDone: (fullText, cid) => {
            if (fullText) this.history[assistantIndex].content = fullText;
            if (cid) this.localConversationId = String(cid);
          },
        };
        const { conversationId } = this.teacher
          ? await ConversationController.startTeacherCompletion(
            { ...args, systemPrompt: this.systemPrompt || null }, callbacks)
          : await ConversationController.startCompletion(args, callbacks);
        const finalId = this.localConversationId || conversationId || null;
        if (finalId) {
          this.localConversationId = String(finalId);
          this.$emit('update:conversationId', this.localConversationId);
          if (wasNew) this.$emit('conversation-created', this.localConversationId);
        }
      } catch (error) {
        this.history[assistantIndex].content = 'Error: failed to get a response. Please try again.';
      } finally {
        this.isStreaming = false;
        this.scrollToBottom();
      }
    },
  },
};
</script>

<style scoped>
.askai-chat {
  min-height: 0;
}
.chat-scroll {
  scroll-behavior: smooth;
  min-height: 0;
}
.chat-input {
  flex-shrink: 0;
}
</style>
